import React, { useEffect, useState, useRef } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import IconButton from "@mui/material/IconButton";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CloseIcon from "@mui/icons-material/Close";
import ReactPrismjs from "@uiw/react-prismjs";
import SendIcon from "@mui/icons-material/Send"; // Import Send icon

import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-perl';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-swift';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-rust';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-scala';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-sql';

import { colorIcon, colorWhite } from "../App/theme";
import PlayArrow from "@mui/icons-material/PlayArrow";
import { executeNodejs } from "../API/CodeExecute";
import { CircularProgress, Tooltip } from "@mui/material";
import { parseJSON } from "../App/utils";
import APIResponseComponent from "../ContentWidgets/APIResponseComponent";
import {generateMsgId} from "../Chat/utils";
import { kbUserData } from "../utils";
import { containsES6SyntaxOrJSX } from "./utils";

export function CodeViewer(props) {
    const [response, setResponse] = useState(null);
    const [codeRunLoading, setCodeRunLoading] = useState(false);
    const [prismHeight, setPrismHeight] = useState(0); // State to store the height of ReactPrismjs
    const responseRef = useRef(null); // Create a reference for the response element
    const prismRef = useRef(null); // Create a reference for the ReactPrismjs element
    const [tooltipOpen, setTooltipOpen] = useState(true);

    const handleTooltipClose = () => {
        setTooltipOpen(false);
    };

    const handleTooltipOpen = () => {
        setTooltipOpen(true);
    };

    const { source, setSystemAlert, onClose, style, styleCopy, noCopy, limitedWidth, styleClose, KB, execute,
        messages, msgIndex, setInputValue, ...restProps } = props;

    const isLastMessage = msgIndex >= (messages?.filter(msg => msg?.content)?.length - 1); // avoids empty msg.content bug

    let canExecute = execute && props?.language?.includes('javascript') && !containsES6SyntaxOrJSX(source);

    useEffect(() => {
        if (response && responseRef.current) {
            responseRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [response]);

    useEffect(() => {
        if (prismRef.current) {
            setPrismHeight(prismRef.current.clientHeight);
        }
    }, [source]);

    const runCode = async () => {
        try {
            const code = source?.includes('module.exports')
                ? source
                : `
            const handler = async (event) => {
                ${source}
            }
            module.exports = {handler}
            `;
            setCodeRunLoading(true);
            const response = await executeNodejs(code, KB);

            setCodeRunLoading(false);

            if (Object.keys(response?.data).length === 1 && response?.data?.error) {
                setResponse(JSON.stringify(response.data));
            } else if (typeof response.data === "object") {
                setResponse(JSON.stringify(response.data));
            } else {
                setResponse(response.data);
            }
        } catch (error) {
            setCodeRunLoading(false);
            setResponse(`Error: ${error?.response?.status || error.message}`);
        }
    };

    const codeViewerStyle = {
        paddingBottom: 2, // avoid scrollbar
        position: 'relative',
        display: 'inline-block',
        maxWidth: '100%',
        minWidth: '350px',
        overflowX: 'auto',
        ...style
    };

    const preStyle = {
        whiteSpace: limitedWidth ? 'pre-wrap' : undefined,
        wordBreak: limitedWidth ? 'break-all' : undefined,
        ...restProps.style
    };

    const oneliner = !(source?.split?.('\n')?.length > 1);
    const handleOnCopy = () => {
        setSystemAlert && setSystemAlert({ msg: 'Copied to clipboard', type: 'success', duration: 1500 })
        if (props?.onCopy) props.onCopy()
    }

    let formattedResponse = parseJSON(response) || { response };

    return (
        <div style={codeViewerStyle}>
            {oneliner && <div style={{ height: '40px' }}></div>}

            {onClose && <IconButton
                onClick={onClose} // Use the onClose prop
                style={styleClose}>
                <CloseIcon style={{ opacity: 0.7, color: colorIcon, fontSize: '1rem' }} />
            </IconButton>}

            <div ref={prismRef}>
                <ReactPrismjs {...{ source, style: preStyle, ...restProps }} />
            </div>
            {!codeRunLoading && <>
                <CopyToClipboard text={source || ''}>
                    <IconButton
                        onClick={handleOnCopy}
                        style={{
                            position: 'absolute',
                            top: `${prismHeight - 30}px`,
                            right: '0px',
                            zIndex: 10,
                            color: 'rgba(11, 11, 11, 0.54)', // optional styling
                        }}>
                        {!noCopy && <FileCopyIcon style={{ opacity: 0.7, color: colorIcon }} />}
                    </IconButton>
                </CopyToClipboard>
                {canExecute && (
                    <Tooltip title="Execute" arrow
                             onMouseEnter={handleTooltipClose}
                             onMouseLeave={handleTooltipOpen}
                             open={isLastMessage && !response && !codeRunLoading && tooltipOpen} placement={"top"}>
                        <IconButton
                            onClick={() => runCode()}
                            style={{
                                position: 'absolute',
                                top: `${prismHeight - 30}px`,
                                right: '36px',
                                zIndex: 10,
                                color: 'rgba(11, 11, 11, 0.54)',
                                animation: isLastMessage && !response && !codeRunLoading && 'pulse 1.5s infinite', // Add pulsing animation
                                ...styleCopy,
                            }}
                        >
                            <PlayArrow style={{ color: colorWhite }} />
                        </IconButton>
                    </Tooltip>
                )}
            </>}

            {codeRunLoading
                ? <CircularProgress />
                : response && (
                <div ref={responseRef} style={{ position: 'relative', paddingTop: '30px'  }}>

                    <APIResponseComponent open={false} showResponseSize={true} JSONData={formattedResponse} enableClipboard={true} />

                    {<>
                        <CopyToClipboard text={response || ''}>
                            <IconButton
                                onClick={handleOnCopy}
                                style={{
                                    position: 'absolute',
                                    top: '35px',
                                    right: '0px',
                                    zIndex: 10,
                                    color: 'rgba(11, 11, 11, 0.54)', // optional styling
                                }}>
                                <FileCopyIcon style={{ opacity: 0.7, color: colorIcon }} />
                            </IconButton>
                        </CopyToClipboard>
                        {!!props.RequestChatAPI && isLastMessage && <Tooltip title="Send Response" arrow open={isLastMessage} placement={"top"} >
                            <IconButton
                                onClick={ async () => {
                                    await props.RequestChatAPI([...messages, {
                                        role: 'user',
                                        content: typeof response === 'number' ? response.toString() : response,
                                        userId: kbUserData().chatUsername,
                                        msgId: generateMsgId()
                                    }])
                                }}
                                style={{
                                    position: 'absolute',
                                    top: '35px',
                                    right: '36px',
                                    zIndex: 10,
                                    color: 'rgba(11, 11, 11, 0.54)', // optional styling
                                    animation: 'pulse 1.5s infinite', // Add pulsing animation,
                                }}
                            >
                                <SendIcon style={{ opacity: 0.9, color: colorIcon }} />
                            </IconButton>
                        </Tooltip>}
                    </>}
                </div>
            )
            }
        </div>
    );
}